import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import product components (lazy-loaded for performance)
const AllProducts = React.lazy(() => import("./AllProducts"));
const NewProductForm = React.lazy(() => import("./NewProductForm"));
const ProductView = React.lazy(() => import("./ProductView"));
const Cart = React.lazy(() => import("./Cart"));
const OrderDetails = React.lazy(() => import("./OrderDetails"));

/**
 * Products Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (products/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/products/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const productsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "products",
    name: "Products & Inventory",
    description: "Sell products and manage inventory",
    version: "1.2.1",
    enabled: true,
    category: "ecommerce",
    order: 4,
    icon: "🛒",
    color: "#D69E2E",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/products",
            component: AllProducts,
            permissionLevel: "view",
        },
        {
            path: "/products/new",
            component: NewProductForm,
            permissionLevel: "create",
        },
        {
            path: "/products/:id",
            component: ProductView,
            permissionLevel: "view",
        },
        {
            path: "/cart",
            component: Cart,
            permissionLevel: "view",
        },
        {
            path: "/order/:id",
            component: OrderDetails,
            permissionLevel: "view",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Products",
            path: "/products",
            icon: "📦",
            permissionLevel: "view",
        },
        {
            label: "New Product",
            path: "/products/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Shopping Cart",
            path: "/cart",
            icon: "🛒",
            permissionLevel: "view",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/products/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Add Product",
            path: "/products/new",
            icon: "➕",
            description: "Add a new product to your catalog"
        },
        {
            label: "View Cart",
            path: "/cart",
            icon: "🛒",
            description: "Review items in shopping cart"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.2.1",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready e-commerce platform",
                "✅ Complete product catalog management",
                "✅ Advanced seller profile integration",
                "✅ IPFS media storage integration",
                "✅ Full shopping cart and checkout system",
                "✅ Comprehensive order management"
            ],
            notes: "Official stable release with complete e-commerce functionality."
        },
        {
            version: "1.2.0",
            releaseDate: "2025-10-31",
            title: "IPFS Media Integration",
            features: [
                "✨ Integrated IPFS server for product images",
                "✨ Decentralized media storage",
                "✨ Improved image handling and loading",
                "✨ Enhanced media persistence"
            ],
            notes: "Major upgrade with IPFS integration for decentralized product media storage."
        },
        {
            version: "1.1.0",
            releaseDate: "2025-09-20",
            title: "Theme System & SEO Enhancement",
            features: [
                "✨ Full light/dark mode support across all product pages",
                "✨ Enhanced Cart with theme-aware styling",
                "✨ Improved page title management with usePageTitle hook",
                "✨ Better SEO optimization across product pages",
                "✨ Theme migration documentation"
            ],
            notes: "Major UI overhaul with comprehensive theme system and SEO improvements."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-09-14",
            title: "Component Architecture Optimization",
            features: [
                "🔨 Created ShippingAddressModal component",
                "🔨 Created SortableImage component for product images",
                "🔨 Removed legacy TagInput component",
                "🔨 Cleaned up unused components",
                "✨ Improved component organization",
                "✨ Enhanced GraphQL code generation support"
            ],
            notes: "Component architecture optimization with better code organization."
        },
        {
            version: "0.9.0",
            releaseDate: "2025-08-08",
            title: "Brand Consistency & UI Polish",
            features: [
                "✨ Enhanced brand configuration integration",
                "✨ Improved UI consistency across all product pages",
                "✨ Updated responsive design",
                "✨ Better color scheme integration"
            ],
            notes: "UI polish and brand consistency improvements."
        },
        {
            version: "0.8.0",
            releaseDate: "2024-07-14",
            title: "Module Configuration System",
            features: [
                "✅ Created moduleConfig.ts structure",
                "✅ Defined routes and navigation (99 lines)",
                "✅ Configured permission system",
                "✅ Added quick actions",
                "✅ Integrated with profile management"
            ],
            notes: "Established formal module configuration architecture."
        },
        {
            version: "0.7.0",
            releaseDate: "2024-07-13",
            title: "Authentication System Integration",
            features: [
                "🔒 Integrated centralized authentication module",
                "🔒 Updated NewProductForm authentication",
                "🔒 Updated ProductView authentication",
                "✨ Improved auth component imports"
            ],
            notes: "Enhanced security with centralized authentication integration."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-07-01",
            title: "Advanced Filtering & Life Essential Club",
            features: [
                "✨ Major AllProducts enhancement (248 lines added)",
                "✨ Improved filtering and search capabilities",
                "✨ Added Life Essential Club category features",
                "✨ Enhanced NewProductForm with club categories",
                "✨ Updated ProductView with club product display",
                "✨ Advanced product categorization"
            ],
            notes: "Major feature expansion with Life Essential Club integration and advanced filtering."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-06-30",
            title: "Seller Profiles & Syndication",
            features: [
                "✨ Added seller profile management (363 lines)",
                "✨ Product syndication options",
                "✨ Enhanced AllProducts with seller filtering",
                "✨ Integrated seller profiles in NewProductForm",
                "✨ Seller information in ProductView"
            ],
            notes: "Marketplace features with multi-seller support and syndication."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-06-29",
            title: "Product View Enhancement",
            features: [
                "✨ Massive ProductView enhancement (301 lines added)",
                "✨ Seller profile display and management",
                "✨ Enhanced product details view",
                "✨ Improved product editing interface"
            ],
            notes: "Major product viewing experience upgrade."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-06-28",
            title: "Extended Product Fields",
            features: [
                "✨ Added extensive product fields (578 lines)",
                "✨ Enhanced product metadata",
                "✨ Removed AuthButton component",
                "✨ Integrated LoginWithSmsModal",
                "✨ Improved product form validation"
            ],
            notes: "Comprehensive product information management system."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-06-17",
            title: "UI/UX Refactoring & Stripe Integration",
            features: [
                "🔨 Major refactoring (1,652 insertions, 1,803 deletions)",
                "✨ Enhanced UI consistency with brand configuration",
                "✨ Updated AllProducts search and filtering",
                "✨ Improved Cart layout and UX",
                "✨ Enhanced NewProductForm (1,338 lines)",
                "✨ Better OrderDetails with Stripe integration",
                "✨ Refined ProductView interface",
                "✨ Responsive design improvements"
            ],
            notes: "Massive refactoring improving code quality and user experience across all product pages."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-06-14",
            title: "Initial E-Commerce Platform",
            features: [
                "✅ Created AllProducts catalog (746 lines)",
                "✅ Created NewProductForm (1,061 lines)",
                "✅ Created ProductView (1,119 lines)",
                "✅ Created Shopping Cart (438 lines)",
                "✅ Created OrderDetails (775 lines)",
                "✅ Created AuthButton component",
                "✅ Complete e-commerce workflow",
                "✅ Product CRUD operations",
                "✅ Shopping cart functionality",
                "✅ Order management system"
            ],
            notes: "Initial implementation with complete e-commerce platform (4,188 lines of code)."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default productsModuleConfig;